/**
 * Which copy of a device the Settings modal should believe.
 *
 * 🔴 Measured 2026-08-23: the modal was serving an hours-old snapshot as live.
 * The admin fleet table hands the modal `_apiDevice` — the raw row it drew
 * with — frozen into the UI store at click time. On the admin path the
 * operator store always misses (it holds only the logged-in admin's own
 * devices), so that frozen row was the ONLY source, and it never updated. The
 * status badge, the NodeMCU state behind "Degraded", and the Connection
 * column could all be hours stale while being read as the present tense. That
 * is why the fleet column and the Connection tab disagreed.
 *
 * The snapshot still cannot be dropped: it is the only thing to show in the
 * beat between the modal opening and the fresh fetch landing, and it carries
 * another tenant's device, which no store of ours holds.
 */

/**
 * How often the admin fleet list re-asks the server.
 *
 * Bounded from both sides. It must be FASTER than the 240s at which
 * `isReportStale()` calls a report dead — otherwise a healthy device drifts
 * into "stale" purely because we had not asked, which is the same lie moved
 * one layer out. And it must be slow enough that an admin panel left open all
 * day does not hammer an aggregate that touches every device in the fleet.
 */
export const FLEET_REFETCH_MS = 60_000;

export function pickApiDevice<T extends { _id: string }>(
   /** The operator store — live, but only ever holds the admin's own devices. */
   storeDevice: T | undefined,
   /** This device's row out of the live admin fleet query. */
   freshDevice: T | undefined,
   /** `_apiDevice`: the row the fleet table drew with, frozen at click time. */
   snapshot: T | undefined
): T | undefined {
   // Identity before freshness. A cache entry left over from a
   // previously-opened device would otherwise put another tenant's router
   // password in the WiFi tab — the caller does look the row up by id, but
   // this is not a failure to leave resting on that alone.
   const sameDevice = (d: T | undefined) =>
      d && (!snapshot || d._id === snapshot._id) ? d : undefined;
   return sameDevice(storeDevice) ?? sameDevice(freshDevice) ?? snapshot;
}
